import React from "react";
import {
  ActionIcon,
  Box,
  Collapse,
  NativeSelect,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronDown, IconTrash } from "@tabler/icons-react";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import AdditionalQuestionsSection from "../../components/TransactionDetailModal/TransactionBookedServiceModal/AdditionalQuestionsSection";
import { ServiceRow } from "../../booking/bookingTypes";
import { canDeleteServiceRow } from "../../booking/guards";
import {
  DraftBlockerField,
  rowEndTime,
  serviceLength,
} from "../../booking/draftModel";
import {
  BASE_DURATION_OPTIONS,
  EXTRA_DURATION_OPTIONS,
  clockLabel,
  durationOptions,
} from "./bookingSheetModel";
import type {
  DurationField,
  MobileBookingDraft,
} from "./useMobileBookingDraft";
import { MobileDateField } from "../sheets/MobileDateField";
import styles from "./MobileBooking.module.css";

/** Inherited-field tooltip (addendum §4 copy). */
export const SECONDARY_DATE_TITLE =
  "Secondary services follow the first service's date.";

const ICON = { size: 16, stroke: 1.8 } as const;

export interface MobileServiceCardProps {
  draft: MobileBookingDraft;
  row: ServiceRow;
  index: number;
  removing: boolean;
  durOpen: boolean;
  adjOpen: boolean;
  onTrash: () => void;
  onRemoveCancel: () => void;
  onRemoveConfirm: () => void;
  onPickService: () => void;
  onPickStaff: () => void;
  onToggleDur: () => void;
  onToggleAdj: () => void;
  /** The field flagged by Next on this card (red outline), if any. */
  invalidField?: DraftBlockerField | null;
}

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/**
 * One service card of the draft (README screen 5): swatch + SERVICE N +
 * trash (inline remove confirm), date (secondary cards inherit it), service
 * and provider triggers (full-cover pickers), start time → end + duration, price, and
 * the collapsible duration selects. Overlaps show once, above the sheet
 * footer.
 */
export const MobileServiceCard: React.FC<MobileServiceCardProps> = ({
  draft,
  row,
  index,
  removing,
  durOpen,
  adjOpen,
  onTrash,
  onRemoveCancel,
  onRemoveConfirm,
  onPickService,
  onPickStaff,
  onToggleDur,
  onToggleAdj,
  invalidField = null,
}) => {
  const multi = draft.rows.length > 1;
  const on = !multi || row.uid === draft.activeUid;
  const svc = draft.serviceOf(row);
  const chosen = !!svc;
  const secondary = index > 0;
  const len = serviceLength(row);
  const endText = row.time ? clockLabel(rowEndTime(row)) : "";
  const forms = chosen ? draft.formsFor(row.salonServiceId) : [];

  const staffText = row.employeeId
    ? draft.staffName(row.employeeId) || "Select provider…"
    : "Select provider…";

  const durFields: {
    label: string;
    field: DurationField;
    options: number[];
    none?: string;
  }[] = [
    {
      label: "Base duration",
      field: "baseDuration",
      options: BASE_DURATION_OPTIONS,
    },
    {
      label: "Processing time",
      field: "processingTime",
      options: EXTRA_DURATION_OPTIONS,
      none: "None",
    },
    {
      label: "Finishing time",
      field: "finishingTime",
      options: EXTRA_DURATION_OPTIONS,
      none: "None",
    },
    {
      label: "Buffer time",
      field: "bufferTime",
      options: EXTRA_DURATION_OPTIONS,
      none: "None",
    },
  ];

  return (
    <Box
      className={styles.card}
      data-on={on}
      data-multi={multi}
      data-testid={`mobile-service-card-${row.uid}`}
      onClick={() => multi && draft.setActive(row.uid)}
    >
      <Box className={styles.cardHead}>
        <Box
          component="span"
          className={styles.swatch}
          style={
            svc?.default_calendar_event_color
              ? { background: svc.default_calendar_event_color }
              : undefined
          }
        />
        <Text component="span" className={styles.badge}>
          {multi ? `Service ${index + 1}` : ""}
        </Text>
        {canDeleteServiceRow(draft.rows, row.uid) && (
          <UnstyledButton
            className={styles.trashBtn}
            onClick={(e) => {
              stop(e);
              onTrash();
            }}
            aria-label="Remove service"
          >
            <IconTrash size={17} stroke={1.8} />
          </UnstyledButton>
        )}
      </Box>

      {removing && (
        <Box className={styles.rmWrap} data-testid="mobile-remove-confirm">
          <Text component="span" className={styles.barText}>
            Remove this service?
          </Text>
          <UnstyledButton
            className={styles.barGhost}
            onClick={(e) => {
              stop(e);
              onRemoveCancel();
            }}
          >
            Cancel
          </UnstyledButton>
          <UnstyledButton
            className={styles.barDanger}
            onClick={(e) => {
              stop(e);
              onRemoveConfirm();
            }}
          >
            Remove
          </UnstyledButton>
        </Box>
      )}

      <Box className={styles.fields}>
        <MobileDateField
          label={secondary ? "Service date" : "Appointment date"}
          value={row.date || ""}
          disabled={secondary}
          title={secondary ? SECONDARY_DATE_TITLE : undefined}
          onChange={(day) => draft.setDate(row.uid, day)}
          className={styles.nativeInput}
        />

        <UnstyledButton
          className={styles.trigger}
          onClick={(e) => {
            stop(e);
            onPickService();
          }}
          aria-label={`Service: ${svc?.name || "Select a service…"}`}
          data-error={invalidField === "service"}
        >
          <Box
            component="span"
            className={styles.triggerText}
            data-set={chosen}
          >
            {svc?.name || "Select a service…"}
          </Box>
          <Box component="span" className={styles.chev}>
            <IconChevronDown {...ICON} />
          </Box>
        </UnstyledButton>

        <UnstyledButton
          className={styles.trigger}
          onClick={(e) => {
            stop(e);
            onPickStaff();
          }}
          aria-label={`Provider: ${staffText}`}
          data-error={invalidField === "provider"}
        >
          <Box
            component="span"
            className={styles.triggerText}
            data-set={!!row.employeeId}
          >
            {staffText}
          </Box>
          <Box component="span" className={styles.chev}>
            <IconChevronDown {...ICON} />
          </Box>
        </UnstyledButton>

        {forms.length > 0 && (
          <Box className={styles.adjWrap} onClick={stop}>
            <AdditionalQuestionsSection
              serviceId={row.salonServiceId}
              activeAdjustmentForms={forms}
              selectedOptions={row.adjustmentOptionIds || []}
              onToggleOption={(optionId, questionId) =>
                draft.toggleAdjustment(row.uid, optionId, questionId)
              }
              isExpanded={adjOpen}
              onToggleExpanded={onToggleAdj}
              labelColor="#555555"
              labelSize="13px"
              labelWeight={600}
              iconSize={16}
            />
          </Box>
        )}

        <Box className={styles.timeRow}>
          <TextInput
            type="time"
            step={900}
            aria-label="Start time"
            value={row.time}
            onChange={(e) => draft.setTime(row.uid, e.currentTarget.value)}
            error={invalidField === "time"}
            onClick={stop}
            classNames={{
              root: styles.timeInputRoot,
              input: styles.nativeInput,
            }}
          />
          <Text component="span" className={styles.toWord}>
            to
          </Text>
          <Box
            className={styles.endField}
            data-testid={`mobile-end-${row.uid}`}
          >
            {endText}
          </Box>
          <Text component="span" className={styles.durText}>
            {chosen ? `${len} min` : ""}
          </Text>
        </Box>

        {chosen && (
          <Box className={styles.priceWrap} aria-label="Price">
            {formatCurrency(draft.priceOf(row))}
          </Box>
        )}
      </Box>

      {chosen && (
        <Box className={styles.durWrap}>
          <UnstyledButton
            className={styles.durToggle}
            onClick={(e) => {
              stop(e);
              onToggleDur();
            }}
            aria-expanded={durOpen}
          >
            Adjust service durations
            <Box
              component="span"
              className={styles.durChev}
              data-open={durOpen}
            >
              <IconChevronDown {...ICON} />
            </Box>
          </UnstyledButton>
          <Collapse in={durOpen}>
            <Box className={styles.durGrid} onClick={stop}>
              {durFields.map((f) => (
                <NativeSelect
                  key={f.field}
                  label={f.label}
                  value={String(row[f.field])}
                  data={durationOptions(f.options, row[f.field], f.none)}
                  onChange={(e) =>
                    draft.setDuration(
                      row.uid,
                      f.field,
                      Number(e.currentTarget.value),
                    )
                  }
                  classNames={{
                    label: styles.durLabel,
                    input: styles.durSelect,
                  }}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};
